/**
 * Common Utility Functions
 * Reusable utility functions for application-wide use
 */

/**
 * Validates if a value is empty using comprehensive type checking.
 * Handles null, undefined, empty strings, empty arrays, empty objects, File objects, and invalid Dates.
 * 
 * Empty Value Definition:
 * - null or undefined: considered empty
 * - string: empty or whitespace-only strings are considered empty
 * - array: arrays with length 0 are considered empty
 * - object: objects with no properties are considered empty (excluding File/FormData with special handling)
 * - File: files with size 0 are considered empty
 * - FormData: always considered non-empty
 * - Date: invalid dates (NaN time) are considered empty
 * - number, boolean, other types: always considered non-empty
 * 
 * @param {any} value - The value to check for emptiness
 * @returns {boolean} True if the value is considered empty, false otherwise
 * 
 * @example
 * isEmpty(null) // Returns true
 * isEmpty(undefined) // Returns true
 * isEmpty('') // Returns true
 * isEmpty('  ') // Returns true (whitespace only)
 * isEmpty([]) // Returns true
 * isEmpty({}) // Returns true
 * isEmpty('hello') // Returns false
 * isEmpty([1, 2, 3]) // Returns false
 * isEmpty({ id: 1 }) // Returns false
 * isEmpty(new File([], 'test.txt')) // Returns true (0 byte file)
 * isEmpty(new File(['content'], 'test.txt')) // Returns false
 * isEmpty(new Date(NaN)) // Returns true (invalid date)
 * isEmpty(new Date()) // Returns false (valid date)
 * isEmpty(0) // Returns false (numbers are always non-empty)
 * isEmpty(false) // Returns false (booleans are always non-empty)
 */
export function isEmpty(value: any): boolean {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim().length === 0;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  // Special handling for Date objects (invalid dates are considered empty)
  if (value instanceof Date) {
    return isNaN(value.getTime());
  }

  // Special handling for File objects
  if (value instanceof File) {
    return value.size === 0;
  }

  // FormData is always considered non-empty
  if (value instanceof FormData) {
    return false;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  return false;
}
